import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {NgClass, NgOptimizedImage} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ParamMap} from '@angular/router';
import {TranslatePipe} from '@ngx-translate/core';
import {forkJoin, Observable, switchMap, tap} from 'rxjs';
import {ProductService} from '../../../../services/product/product.service';
import {OfferService} from '../../../../services/offer/offer.service';
import {UserService} from '../../../../services/user/user.service';
import {PartnerService} from '../../../../services/partner/partner.service';
import {AuthService} from '../../../../services/auth/auth.service';
import {LandingPageService} from '../../../../services/landing-page/landing-page.service';
import {AdvertisingPlatformService} from '../../../../services/advertising-platform/advertising-platform.service';
import {CountryService} from '../../../../services/country/country.service';
import {LangService} from '../../../../services/language/lang.service';
import {Product} from '../../../../models/product';
import {Offer} from '../../../../models/offer';
import {User} from '../../../../models/user';
import {Partner} from '../../../../models/partner/partner';
import {Country} from '../../../../models/country';
import {LandingPage} from '../../../../models/landing-page/landing-page';
import {AdvertisingPlatform} from '../../../../models/advertising-platform';
import {Language} from '../../../../models/language';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import {ElementLoaderDirective} from '../../../../directives/element-loader/element-loader.directive';
import {LandingPageActivityPeriods} from '../../../../constants/landing-page-activity';
import {StatsGoalTypes} from '../../../../constants/stats-goal-types';
import {Pool} from '../../../../models/landing-page/pool';
import {PoolService} from '../../../../services/landing-page/pool.service';
import {Config} from '../../../../models/config';

@Component({
  selector: 'app-stats-filters',
  imports: [
    FormsModule,
    NgClass,
    NgOptimizedImage,
    TranslatePipe,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    ElementLoaderDirective,
  ],
  templateUrl: './stats-filters.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './stats-filters.component.scss'
})
export class StatsFiltersComponent implements OnInit {
  @Input() initialParams: ParamMap | null = null;
  @Output() ready = new EventEmitter<void>();

  products: Product[] = [];
  offers: Offer[] = [];
  accountManagers: User[] = [];
  partners: Partner[] = [];
  countries: Country[] = [];
  landingPages: LandingPage[] = [];
  pools: Pool[] = [];
  languages: Language[] = [];
  advertisingPlatformList: AdvertisingPlatform[] = [];
  partnerAccountTypeList: string[] = [];

  offerLoading = false;
  partnerLoading = false;
  landingPageLoading = false;

  activityPeriods = LandingPageActivityPeriods;
  goalTypeNameList = StatsGoalTypes;

  filters: any = this.createDefaultFilters();

  constructor(
    private productService: ProductService,
    private offerService: OfferService,
    private userService: UserService,
    private partnerService: PartnerService,
    private auth: AuthService,
    private landingPageService: LandingPageService,
    private poolService: PoolService,
    private advertisingPlatformService: AdvertisingPlatformService,
    protected countryService: CountryService,
    protected langService: LangService,
  ) {
  }

  ngOnInit(): void {
    forkJoin([
      this.productService.findAll({sort: 'name'}),
      this.userService.findAll({accountManager: true, sort: 'firstname,lastname'}),
      this.countryService.findAll(),
      this.auth.getAsyncConfig(),
      this.advertisingPlatformService.getList(),
      this.langService.getLanguages(),
      this.poolService.findAll({sort: 'name'}),
    ]).pipe(
      switchMap(([products, accountManagers, countries, config, advertisingPlatforms, languages, pools]: [Product[], User[], Country[], Config, AdvertisingPlatform[], Language[], Pool[]]) => {
        this.products = products;
        this.accountManagers = accountManagers;
        this.countries = countries;
        this.advertisingPlatformList = advertisingPlatforms;
        this.languages = languages;
        this.partnerAccountTypeList = config?.data?.partnerAccountTypeList;
        this.pools = pools;

        this.applyScalarParams();

        return forkJoin([this.loadOffers(), this.loadPartners()]);
      }),
      tap(() => {
        const offerId = this.getNumberParam('offerId');
        this.filters.offer = offerId ? this.offers.find(offer => offer.id === offerId) ?? null : null;

        const partnerIds = this.getParam('partnerId');
        this.filters.partner = partnerIds ? partnerIds.split(',').map(Number).filter(id => id) : [];
      }),
      switchMap(() => this.loadLandingPages()),
    ).subscribe(() => {
      const landingPageId = this.getNumberParam('landingPageId');
      this.filters.landingPage = landingPageId
        ? this.landingPages.find(landingPage => landingPage.id === landingPageId) ?? null
        : null;

      this.ready.emit();
    });
  }

  getCriteria(): any {
    const criteria: any = {};

    if (this.filters.product?.id) {
      criteria.productId = this.filters.product.id;
    }

    if (this.filters.offer?.id) {
      criteria.offerId = this.filters.offer.id;
    }

    if (this.filters.partner.length) {
      criteria.partnerId = this.filters.partner;
    }

    if (this.filters.accountManager) {
      criteria.accountManagerId = this.filters.accountManager.id;
    }

    if (this.filters.accountType) {
      criteria.accountType = this.filters.accountType;
    }

    if (this.isMediaBuy() && this.filters.advertisingPlatform) {
      criteria.advertisingPlatformId = this.filters.advertisingPlatform;
    }

    if (this.filters.landingPage?.id) {
      criteria.landingPageId = this.filters.landingPage.id;
    } else if (this.filters.landingPageActivity) {
      criteria.landingPageActiveSince = this.filters.landingPageActivity;
    }

    if (this.filters.pool?.id) {
      criteria.poolId = this.filters.pool.id;
    }

    for (const field of ['subid', 'tc1', 'tc2', 'tc3', 'tc4', 'source', 'country', 'lang']) {
      if (this.filters[field]) {
        criteria[field] = this.filters[field];
      }
    }

    if (this.filters.coreg !== null) {
      criteria.coreg = this.filters.coreg;
    }

    if (this.filters.cross !== null) {
      criteria.cross = this.filters.cross;
    }

    if (this.filters.paid !== null) {
      criteria.paid = this.filters.paid ? 1 : 0;
    }

    if (this.filters.goalTypeNames.length < this.goalTypeNameList.length) {
      criteria.goalTypeNames = this.filters.goalTypeNames.join(',');
    }

    return criteria;
  }

  reset(): void {
    this.filters = this.createDefaultFilters();
    this.refreshOffers();
    this.refreshPartners();
    this.refreshLandingPages();
  }

  isMediaBuy(): boolean {
    return this.filters.accountType === 'mediabuy';
  }

  changeProduct(): void {
    this.refreshOffers();
  }

  changeOffer(): void {
    this.refreshLandingPages();
  }

  refreshOffers(): void {
    this.loadOffers().subscribe(() => this.filters.offer = null);
  }

  refreshPartners(): void {
    this.loadPartners().subscribe(() => this.filters.partner = []);
  }

  refreshLandingPages(): void {
    this.loadLandingPages().subscribe(() => this.filters.landingPage = null);
  }

  getProductPlaceholder(): string {
    return this.products.length ? ('All ' + this.products.length + ' product(s)') : 'No product';
  }

  getOfferPlaceholder(): string {
    return this.offers.length ? ('All ' + this.offers.length + ' offer(s)') : 'No offer';
  }

  getPoolPlaceholder(): string {
    return this.pools.length ? ('All ' + this.pools.length + ' pool(s)') : 'No pool';
  }

  getLandingPagePlaceholder(): string {
    const active = this.filters.landingPageActivity ? ' active' : '';

    return this.landingPages.length
      ? ('All ' + this.landingPages.length + active + ' promotion link(s)')
      : 'No' + active + ' promotion link';
  }

  private createDefaultFilters(): any {
    return {
      product: null,
      offer: null,
      accountType: null,
      advertisingPlatform: null,
      accountManager: null,
      partner: [],
      country: null,
      lang: null,
      landingPage: null,
      landingPageActivity: null,
      pool: null,
      subid: '',
      tc1: '',
      tc2: '',
      tc3: '',
      tc4: '',
      source: '',
      coreg: null,
      cross: null,
      paid: null,
      goalTypeNames: StatsGoalTypes.map(goalType => goalType.key),
    };
  }

  private loadOffers(): Observable<Offer[]> {
    this.offerLoading = true;

    const params: any = {};

    if (this.filters.product?.id) {
      params.productId = this.filters.product.id;
    }

    return this.offerService.findAll(params).pipe(tap(offers => {
      this.offers = offers;
      this.offerLoading = false;
    }));
  }

  private loadPartners(): Observable<Partner[]> {
    this.partnerLoading = true;

    const params: any = {
      sort: 'companyName',
      order: 'asc',
      status: 'active'
    };

    if (this.filters.accountType) {
      params.accountType = this.filters.accountType;
    }

    if (this.filters.accountManager?.id) {
      params.accountManagerId = this.filters.accountManager.id;
    }

    if (this.isMediaBuy() && this.filters.advertisingPlatform) {
      params.advertisingPlatform = this.filters.advertisingPlatform;
    }

    return this.partnerService.findAll(params).pipe(tap(partners => {
      this.partners = partners;
      this.partnerLoading = false;
    }));
  }

  private loadLandingPages(): Observable<LandingPage[]> {
    this.landingPageLoading = true;

    const params: any = {
      sort: 'name',
      order: 'asc'
    };

    if (this.filters.offer?.id) {
      params.offerId = this.filters.offer.id;
    }

    if (this.filters.landingPageActivity) {
      params.activeSince = this.filters.landingPageActivity;
    }

    return this.landingPageService.findAll(params).pipe(tap(landingPages => {
      this.landingPages = landingPages;
      this.landingPageLoading = false;
    }));
  }

  private applyScalarParams(): void {
    if (!this.initialParams) {
      return;
    }

    const productId = this.getNumberParam('productId');
    this.filters.product = productId ? this.products.find(product => product.id === productId) ?? null : null;

    const accountManagerId = this.getNumberParam('accountManagerId');
    this.filters.accountManager = accountManagerId
      ? this.accountManagers.find(accountManager => accountManager.id === accountManagerId) ?? null
      : null;

    this.filters.accountType = this.getParam('accountType');
    this.filters.advertisingPlatform = this.getParam('advertisingPlatformId');
    this.filters.landingPageActivity = this.getParam('landingPageActiveSince');

    const poolId = this.getNumberParam('poolId');
    this.filters.pool = poolId ? this.pools.find(pool => pool.id === poolId) ?? null : null;

    for (const field of ['subid', 'tc1', 'tc2', 'tc3', 'tc4', 'source']) {
      this.filters[field] = this.getParam(field) ?? '';
    }

    this.filters.country = this.getParam('country');
    this.filters.lang = this.getParam('lang');
    this.filters.coreg = this.getBinaryParam('coreg');
    this.filters.cross = this.getBinaryParam('cross');

    const paid = this.getBinaryParam('paid');
    this.filters.paid = paid === null ? null : paid === 1;

    const goalTypeNames = this.getParam('goalTypeNames');
    if (goalTypeNames) {
      this.filters.goalTypeNames = goalTypeNames.split(',');
    }
  }

  private getParam(name: string): string | null {
    return this.initialParams?.get(name) || null;
  }

  private getNumberParam(name: string): number | null {
    return Number(this.getParam(name)) || null;
  }

  private getBinaryParam(name: string): number | null {
    const value = this.getParam(name);

    return value === '0' || value === '1' ? Number(value) : null;
  }
}
